/* ═══════════════════════════════════════════════════════════════
   SA-003 — FEATURE FLAGS MANAGER
   ═══════════════════════════════════════════════════════════════ */

export type FlagCategory = 'core' | 'social' | 'monetization' | 'streaming' | 'admin' | 'listener' | 'experimental' | 'security';

export type FeatureEnv = 'development' | 'qa' | 'production';

export interface FeatureFlagEnvironment {
  development: boolean;
  qa: boolean;
  production: boolean;
}

export interface FeatureFlagHistoryEntry {
  id: number;
  timestamp: string;
  changedBy: string;
  action: 'created' | 'enabled' | 'disabled' | 'updated' | 'rolled_back';
  environment: FeatureEnv;
  summary: string;
  previousState: string;
  newState: string;
}

export interface FeatureFlagDependency {
  key: string;
  name: string;
  required: boolean;
}

export interface FeatureFlagModule {
  id: string;
  name: string;
  icon: string;
  route: string;
}

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string;
  category: FlagCategory;
  enabled: boolean;
  environments: FeatureFlagEnvironment;
  minVersionRequired: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  dependencies: FeatureFlagDependency[];
  modules: FeatureFlagModule[];
  documentation: string;
  history: FeatureFlagHistoryEntry[];
}

export const flagCategoryMeta: Record<FlagCategory, { label: string; icon: string; color: string; bgColor: string }> = {
  core:          { label: 'Core',           icon: 'ri-coreos-line',         color: 'text-foreground-700', bgColor: 'bg-foreground-100' },
  social:        { label: 'Social',         icon: 'ri-group-line',          color: 'text-emerald-600',    bgColor: 'bg-emerald-100' },
  monetization:  { label: 'Monetización',   icon: 'ri-money-dollar-circle-line', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  streaming:     { label: 'Streaming',      icon: 'ri-broadcast-line',      color: 'text-red-600',        bgColor: 'bg-red-100' },
  admin:         { label: 'Admin',          icon: 'ri-admin-line',          color: 'text-sky-600',        bgColor: 'bg-sky-100' },
  listener:      { label: 'Oyente',         icon: 'ri-user-voice-line',     color: 'text-primary-600',    bgColor: 'bg-primary-100' },
  experimental:  { label: 'Experimental',   icon: 'ri-flask-line',          color: 'text-purple-600',     bgColor: 'bg-purple-100' },
  security:      { label: 'Seguridad',      icon: 'ri-shield-check-line',   color: 'text-rose-600',       bgColor: 'bg-rose-100' },
};

export const featureFlags: FeatureFlag[] = [
  {
    id: 'ff-001',
    key: 'cabina_2.0',
    name: 'Cabina 2.0',
    description: 'Nueva versión de la cabina de transmisión con IA, cola inteligente, moderación automática y panel de métricas en tiempo real.',
    category: 'core',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.2.0',
    createdAt: '2025-11-15',
    updatedAt: '2026-07-10',
    updatedBy: 'Carlos Mendoza',
    dependencies: [
      { key: 'live_chat_unificado', name: 'Chat Unificado', required: true },
      { key: 'ia_moderacion', name: 'IA Moderación', required: false },
    ],
    modules: [
      { id: 'mod-cabina', name: 'Cabina de Transmisión', icon: 'ri-mic-line', route: '/admin/cabina' },
      { id: 'mod-broadcast', name: 'Centro de Transmisión', icon: 'ri-broadcast-line', route: '/admin/broadcast-center' },
      { id: 'mod-studio', name: 'Studio', icon: 'ri-movie-line', route: '/admin/studio' },
    ],
    documentation: 'La Cabina 2.0 introduce un panel unificado con IA para recomendaciones de canciones, cola inteligente que aprende del comportamiento de la audiencia, y moderación automática de mensajes. Requiere el Chat Unificado activado. Compatible con todos los planes desde Pro.',
    history: [
      { id: 101, timestamp: '2026-07-10T14:22:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'production', summary: 'Activado en producción tras 2 semanas en QA sin incidentes.', previousState: 'production: false', newState: 'production: true' },
      { id: 100, timestamp: '2026-06-25T09:10:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'qa', summary: 'Promovido a QA para pruebas con 50 radios beta.', previousState: 'qa: false', newState: 'qa: true' },
      { id: 99, timestamp: '2026-06-01T11:00:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'development', summary: 'Actualización con mejoras en latencia del panel.', previousState: 'v4.1.0', newState: 'v4.2.0' },
      { id: 98, timestamp: '2025-11-15T08:30:00Z', changedBy: 'Carlos Mendoza', action: 'created', environment: 'development', summary: 'Feature flag creado. Desarrollo inicial.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-002',
    key: 'live_chat_unificado',
    name: 'Chat Unificado',
    description: 'Sistema de chat que unifica mensajes de cabina, comunidad, lives y mensajes privados en una sola interfaz.',
    category: 'social',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.1.0',
    createdAt: '2025-10-01',
    updatedAt: '2026-07-05',
    updatedBy: 'María López',
    dependencies: [],
    modules: [
      { id: 'mod-live-chat', name: 'Live Chat', icon: 'ri-chat-3-line', route: '/admin/live-chat' },
      { id: 'mod-unified-chat', name: 'Chat Unificado', icon: 'ri-chat-smile-2-line', route: '/admin/unified-chat' },
      { id: 'mod-community', name: 'Comunidad', icon: 'ri-group-line', route: '/admin/community' },
    ],
    documentation: 'El Chat Unificado consolida todos los canales de mensajería de una radio en una sola vista. Soporta mensajes de cabina, comunidad, lives y mensajes privados con filtros por tipo. Compatible con planes Free y superiores.',
    history: [
      { id: 90, timestamp: '2026-07-05T16:45:00Z', changedBy: 'María López', action: 'updated', environment: 'production', summary: 'Actualización de rendimiento — reducción de latencia en 40%.', previousState: 'v4.0.2', newState: 'v4.1.0' },
      { id: 89, timestamp: '2026-03-15T10:00:00Z', changedBy: 'María López', action: 'enabled', environment: 'production', summary: 'Activado para todas las radios.', previousState: 'production: false', newState: 'production: true' },
      { id: 88, timestamp: '2025-10-01T09:00:00Z', changedBy: 'María López', action: 'created', environment: 'development', summary: 'Creación del feature flag.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-003',
    key: 'top_10_categoria',
    name: 'Top 10 por Categoría',
    description: 'Ranking de las 10 mejores radios por categoría musical. Los oyentes pueden filtrar por género.',
    category: 'listener',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.3.0',
    createdAt: '2026-05-20',
    updatedAt: '2026-07-08',
    updatedBy: 'Diego Castillo',
    dependencies: [
      { key: 'sistema_rankings', name: 'Sistema de Rankings', required: true },
    ],
    modules: [
      { id: 'mod-rankings', name: 'Rankings', icon: 'ri-bar-chart-2-line', route: '/rankings' },
      { id: 'mod-top10', name: 'Top 10 Global', icon: 'ri-medal-line', route: '/top-10-global' },
      { id: 'mod-top10cat', name: 'Top 10 Categoría', icon: 'ri-pie-chart-line', route: '/top-10-categoria' },
      { id: 'mod-top10pais', name: 'Top 10 País', icon: 'ri-earth-line', route: '/top-10-pais' },
    ],
    documentation: 'El Top 10 por Categoría permite filtrar rankings por géneros musicales: Latin, Rock, Pop, Electrónica, Urbano, Clásica y más. Usa el Score RFW para ordenar. Se actualiza cada 15 minutos.',
    history: [
      { id: 80, timestamp: '2026-07-08T12:30:00Z', changedBy: 'Diego Castillo', action: 'enabled', environment: 'production', summary: 'Lanzamiento público. Activado en producción.', previousState: 'production: false', newState: 'production: true' },
      { id: 79, timestamp: '2026-06-28T14:00:00Z', changedBy: 'Diego Castillo', action: 'enabled', environment: 'qa', summary: 'QA testing completado con 200 categorías distintas.', previousState: 'qa: false', newState: 'qa: true' },
      { id: 78, timestamp: '2026-05-20T10:15:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'development', summary: 'Desarrollo inicial del Top 10 por Categoría.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-004',
    key: 'ia_moderacion',
    name: 'IA Moderación',
    description: 'Sistema de moderación automática basado en IA que detecta spam, lenguaje ofensivo, links maliciosos y comportamientos sospechosos.',
    category: 'security',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.0.0',
    createdAt: '2025-09-10',
    updatedAt: '2026-07-01',
    updatedBy: 'Ana Rivera',
    dependencies: [],
    modules: [
      { id: 'mod-cabina', name: 'Cabina de Transmisión', icon: 'ri-mic-line', route: '/admin/cabina' },
      { id: 'mod-moderacion', name: 'Moderación', icon: 'ri-shield-line', route: '/admin/cabina/moderacion' },
      { id: 'mod-global-mod', name: 'Moderación Global', icon: 'ri-shield-star-line', route: '/sa/global-moderation' },
      { id: 'mod-community', name: 'Comunidad', icon: 'ri-group-line', route: '/admin/community' },
      { id: 'mod-live-chat', name: 'Live Chat', icon: 'ri-chat-3-line', route: '/admin/live-chat' },
    ],
    documentation: 'La IA de Moderación analiza mensajes en tiempo real usando NLP. Detecta: spam (97% precisión), lenguaje ofensivo (94%), links maliciosos (99%), y comportamientos de flood. Los mensajes marcados van a una cola de revisión manual. Configurable por radio.',
    history: [
      { id: 70, timestamp: '2026-07-01T08:00:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'production', summary: 'Modelo NLP actualizado — mejora del 5% en precisión de spam.', previousState: 'v3.9.1', newState: 'v4.0.0' },
      { id: 69, timestamp: '2026-04-10T11:20:00Z', changedBy: 'Ana Rivera', action: 'enabled', environment: 'production', summary: 'Activado para todas las radios con plan Pro+.', previousState: 'production: false', newState: 'production: true' },
      { id: 68, timestamp: '2025-09-10T15:30:00Z', changedBy: 'Ana Rivera', action: 'created', environment: 'development', summary: 'Inicio del desarrollo de IA Moderación.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-005',
    key: 'stripe_pagos',
    name: 'Pagos con Stripe',
    description: 'Integración con Stripe para procesar pagos de planes, frecuencias premium, licencias anuales y cargos adicionales.',
    category: 'monetization',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.0.0',
    createdAt: '2025-08-01',
    updatedAt: '2026-06-20',
    updatedBy: 'Carlos Mendoza',
    dependencies: [],
    modules: [
      { id: 'mod-pricing', name: 'Pricing', icon: 'ri-price-tag-3-line', route: '/pricing' },
      { id: 'mod-plans', name: 'Planes SA', icon: 'ri-stack-line', route: '/sa/plans' },
      { id: 'mod-payments', name: 'Pagos SA', icon: 'ri-bank-card-line', route: '/sa/payments' },
      { id: 'mod-billing', name: 'Facturación SA', icon: 'ri-bill-line', route: '/sa/billing' },
    ],
    documentation: 'Stripe procesa todos los pagos de la plataforma. Soporta: tarjetas de crédito/débito, ACH, y wallets (Apple Pay, Google Pay). Webhooks configurados para: payment_intent.succeeded, invoice.paid, subscription.updated. No almacenamos datos de tarjeta.',
    history: [
      { id: 60, timestamp: '2026-06-20T09:45:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'production', summary: 'Agregado soporte para Apple Pay y Google Pay.', previousState: 'v3.9.0', newState: 'v4.0.0' },
      { id: 59, timestamp: '2025-12-01T16:00:00Z', changedBy: 'Carlos Mendoza', action: 'enabled', environment: 'production', summary: 'Stripe activado para todos los pagos.', previousState: 'production: false', newState: 'production: true' },
      { id: 58, timestamp: '2025-08-01T10:00:00Z', changedBy: 'Carlos Mendoza', action: 'created', environment: 'development', summary: 'Inicio de integración con Stripe.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-006',
    key: 'paypal_pagos',
    name: 'Pagos con PayPal',
    description: 'Alternativa de pago con PayPal para usuarios que prefieren este método. Integración vía PayPal Checkout SDK.',
    category: 'monetization',
    enabled: true,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.3.0',
    createdAt: '2026-04-15',
    updatedAt: '2026-07-11',
    updatedBy: 'Carlos Mendoza',
    dependencies: [
      { key: 'stripe_pagos', name: 'Pagos con Stripe', required: false },
    ],
    modules: [
      { id: 'mod-payments', name: 'Pagos SA', icon: 'ri-bank-card-line', route: '/sa/payments' },
      { id: 'mod-pricing', name: 'Pricing', icon: 'ri-price-tag-3-line', route: '/pricing' },
    ],
    documentation: 'PayPal Checkout como alternativa a Stripe. El usuario puede elegir entre Stripe o PayPal al pagar. La integración usa el SDK oficial de PayPal. Requiere configuración de Client ID y Secret en Supabase Secrets.',
    history: [
      { id: 55, timestamp: '2026-07-11T13:00:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'development', summary: 'Refactor del flujo de checkout — ahora es modal en vez de redirect.', previousState: 'v4.2.5', newState: 'v4.3.0' },
      { id: 54, timestamp: '2026-06-15T10:30:00Z', changedBy: 'Carlos Mendoza', action: 'disabled', environment: 'qa', summary: 'Revertido de QA por bug en conversión de moneda.', previousState: 'qa: true', newState: 'qa: false' },
      { id: 53, timestamp: '2026-04-15T08:00:00Z', changedBy: 'Carlos Mendoza', action: 'created', environment: 'development', summary: 'Creación del feature flag de PayPal.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-007',
    key: 'sistema_rankings',
    name: 'Sistema de Rankings',
    description: 'Motor de rankings que calcula posiciones basadas en Score RFW. Soporta rankings globales, por país y por categoría.',
    category: 'core',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '3.5.0',
    createdAt: '2025-06-01',
    updatedAt: '2026-07-05',
    updatedBy: 'Diego Castillo',
    dependencies: [],
    modules: [
      { id: 'mod-rankings', name: 'Rankings', icon: 'ri-bar-chart-2-line', route: '/rankings' },
      { id: 'mod-top10', name: 'Top 10 Global', icon: 'ri-medal-line', route: '/top-10-global' },
      { id: 'mod-top10cat', name: 'Top 10 Categoría', icon: 'ri-pie-chart-line', route: '/top-10-categoria' },
      { id: 'mod-top10pais', name: 'Top 10 País', icon: 'ri-earth-line', route: '/top-10-pais' },
      { id: 'mod-scores', name: 'Scores RFW', icon: 'ri-line-chart-line', route: '/rankings' },
    ],
    documentation: 'El Sistema de Rankings es el motor central que calcula posiciones usando el algoritmo RFW Score. Pondera: oyentes activos, interacción social, calidad de streaming, y engagement de comunidad. Se recalcula cada hora para rankings globales y cada 30 minutos para rankings en vivo.',
    history: [
      { id: 50, timestamp: '2026-07-05T11:00:00Z', changedBy: 'Diego Castillo', action: 'updated', environment: 'production', summary: 'Optimización: recálculo ahora toma 45s (antes 3min).', previousState: 'v3.4.2', newState: 'v3.5.0' },
      { id: 49, timestamp: '2025-06-01T08:00:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'production', summary: 'Lanzamiento inicial del sistema de rankings.', previousState: '—', newState: 'production: true' },
    ],
  },
  {
    id: 'ff-008',
    key: 'gamificacion_total',
    name: 'Gamificación Total',
    description: 'Sistema completo de gamificación con logros, rachas, niveles, insignias, puntos RFW y leaderboards.',
    category: 'listener',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.2.0',
    createdAt: '2025-12-10',
    updatedAt: '2026-07-09',
    updatedBy: 'María López',
    dependencies: [
      { key: 'sistema_rankings', name: 'Sistema de Rankings', required: true },
    ],
    modules: [
      { id: 'mod-gamificacion', name: 'Gamificación', icon: 'ri-gamepad-line', route: '/gamificacion' },
      { id: 'mod-niveles', name: 'Niveles', icon: 'ri-bar-chart-horizontal-line', route: '/niveles' },
      { id: 'mod-insignias', name: 'Insignias', icon: 'ri-award-line', route: '/insignias' },
      { id: 'mod-logros', name: 'Logros', icon: 'ri-star-line', route: '/logros' },
      { id: 'mod-rachas', name: 'Rachas', icon: 'ri-fire-line', route: '/rachas' },
    ],
    documentation: 'Gamificación Total implementa un ecosistema de recompensas para oyentes. Incluye: 45 logros desbloqueables, 12 niveles con beneficios crecientes, rachas diarias/semanales, y un leaderboard global. Los puntos RFW se pueden canjear por beneficios en radios afiliadas.',
    history: [
      { id: 45, timestamp: '2026-07-09T14:30:00Z', changedBy: 'María López', action: 'updated', environment: 'production', summary: 'Agregados 15 nuevos logros y nivel 12 — Embajador RFW.', previousState: 'v4.1.0', newState: 'v4.2.0' },
      { id: 44, timestamp: '2026-03-01T10:00:00Z', changedBy: 'María López', action: 'enabled', environment: 'production', summary: 'Lanzamiento público de gamificación.', previousState: 'production: false', newState: 'production: true' },
      { id: 43, timestamp: '2025-12-10T09:00:00Z', changedBy: 'María López', action: 'created', environment: 'development', summary: 'Desarrollo inicial de gamificación.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-009',
    key: 'marketplace_publicidad',
    name: 'Marketplace de Publicidad',
    description: 'Plataforma donde anunciantes pueden comprar espacios publicitarios en radios de la red RFW.',
    category: 'monetization',
    enabled: true,
    environments: { development: true, qa: true, production: false },
    minVersionRequired: '4.4.0',
    createdAt: '2026-03-20',
    updatedAt: '2026-07-10',
    updatedBy: 'Carlos Mendoza',
    dependencies: [
      { key: 'stripe_pagos', name: 'Pagos con Stripe', required: true },
    ],
    modules: [
      { id: 'mod-marketplace', name: 'Marketplace Admin', icon: 'ri-store-2-line', route: '/admin/marketplace' },
      { id: 'mod-ad-marketplace', name: 'Marketplace SA', icon: 'ri-store-3-line', route: '/sa/ad-marketplace' },
      { id: 'mod-ad-network', name: 'Red Publicitaria', icon: 'ri-global-line', route: '/sa/ad-network' },
      { id: 'mod-publicidad', name: 'Publicidad', icon: 'ri-advertisement-line', route: '/publicidad' },
    ],
    documentation: 'El Marketplace conecta anunciantes con radios de la red RFW. Los anunciantes pueden: buscar radios por categoría/audiencia, comprar espacios (banner, audio, video), y ver métricas en tiempo real. Las radios reciben 70% de los ingresos. Pagos procesados vía Stripe.',
    history: [
      { id: 40, timestamp: '2026-07-10T16:00:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'qa', summary: 'Última ronda de fixes antes de promoción a producción.', previousState: 'qa: v4.3.1', newState: 'qa: v4.4.0' },
      { id: 39, timestamp: '2026-06-20T09:30:00Z', changedBy: 'Carlos Mendoza', action: 'enabled', environment: 'qa', summary: 'Promovido a QA para pruebas con 20 radios beta.', previousState: 'qa: false', newState: 'qa: true' },
      { id: 38, timestamp: '2026-03-20T11:00:00Z', changedBy: 'Carlos Mendoza', action: 'created', environment: 'development', summary: 'Inicio del desarrollo del marketplace.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-010',
    key: 'app_mobile_notificaciones',
    name: 'App Móvil — Notificaciones Push',
    description: 'Sistema de notificaciones push para las apps móviles (Android/iOS) usando Firebase Cloud Messaging y APNs.',
    category: 'listener',
    enabled: true,
    environments: { development: true, qa: true, production: true },
    minVersionRequired: '4.1.0',
    createdAt: '2025-11-01',
    updatedAt: '2026-06-30',
    updatedBy: 'Diego Castillo',
    dependencies: [],
    modules: [
      { id: 'mod-notifications', name: 'Notificaciones', icon: 'ri-notification-3-line', route: '/notifications' },
      { id: 'mod-app', name: 'App Móvil', icon: 'ri-smartphone-line', route: '/app' },
      { id: 'mod-sa-notifs', name: 'Notificaciones SA', icon: 'ri-notification-4-line', route: '/sa/notifications' },
    ],
    documentation: 'Notificaciones push para las apps móviles. Tipos: alertas de radio en vivo, mensajes de locutores, nuevos episodios, logros desbloqueados, y recordatorios de programas. Los usuarios pueden configurar preferencias por tipo. Envío segmentado por país, idioma e intereses.',
    history: [
      { id: 35, timestamp: '2026-06-30T10:15:00Z', changedBy: 'Diego Castillo', action: 'updated', environment: 'production', summary: 'Agregado envío segmentado por país e intereses.', previousState: 'v4.0.0', newState: 'v4.1.0' },
      { id: 34, timestamp: '2026-02-15T14:00:00Z', changedBy: 'Diego Castillo', action: 'enabled', environment: 'production', summary: 'Push notifications activadas en producción.', previousState: 'production: false', newState: 'production: true' },
      { id: 33, timestamp: '2025-11-01T08:30:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'development', summary: 'Desarrollo inicial de notificaciones push.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-011',
    key: 'discovery_ia',
    name: 'Descubrimiento con IA',
    description: 'Algoritmo de recomendación de radios y programas basado en el comportamiento e intereses del oyente usando IA.',
    category: 'listener',
    enabled: true,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.5.0',
    createdAt: '2026-05-01',
    updatedAt: '2026-07-11',
    updatedBy: 'Ana Rivera',
    dependencies: [
      { key: 'sistema_rankings', name: 'Sistema de Rankings', required: true },
    ],
    modules: [
      { id: 'mod-discovery', name: 'Descubrimiento', icon: 'ri-compass-3-line', route: '/descubrir' },
      { id: 'mod-descubrimiento', name: 'Sistema Descubrimiento', icon: 'ri-radar-line', route: '/sistema-descubrimiento' },
      { id: 'mod-explorar', name: 'Explorar', icon: 'ri-search-eye-line', route: '/explorar' },
    ],
    documentation: 'El motor de Descubrimiento con IA usa collaborative filtering + content-based recommendations para sugerir radios y programas. Aprende de: historial de escucha, reacciones, tiempo de escucha, y follows. Se entrena semanalmente con datos anonimizados.',
    history: [
      { id: 30, timestamp: '2026-07-11T15:45:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'development', summary: 'Modelo actualizado — precisión de recomendación subió de 72% a 84%.', previousState: 'v4.4.0', newState: 'v4.5.0' },
      { id: 29, timestamp: '2026-05-01T09:00:00Z', changedBy: 'Ana Rivera', action: 'created', environment: 'development', summary: 'Inicio de desarrollo del motor de descubrimiento con IA.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-012',
    key: 'modo_oscuro',
    name: 'Modo Oscuro Global',
    description: 'Soporte de tema oscuro en toda la plataforma. Detecta preferencia del sistema y permite toggle manual.',
    category: 'core',
    enabled: true,
    environments: { development: true, qa: true, production: false },
    minVersionRequired: '4.4.0',
    createdAt: '2026-06-01',
    updatedAt: '2026-07-08',
    updatedBy: 'Diego Castillo',
    dependencies: [],
    modules: [
      { id: 'mod-landing', name: 'Landing Page', icon: 'ri-home-line', route: '/' },
      { id: 'mod-admin', name: 'Panel Admin', icon: 'ri-dashboard-line', route: '/admin/dashboard' },
      { id: 'mod-sa', name: 'Super Admin', icon: 'ri-admin-line', route: '/sa/dashboard' },
      { id: 'mod-radio', name: 'Perfil Radio', icon: 'ri-radio-line', route: '/radio/:frequency' },
      { id: 'mod-app', name: 'App Móvil', icon: 'ri-smartphone-line', route: '/app' },
    ],
    documentation: 'Modo Oscuro Global aplica un tema dark en todas las páginas. Usa variables CSS del StyleSystem para consistencia. El toggle se guarda en localStorage. Respeta la preferencia del sistema (prefers-color-scheme). Compatible con todos los navegadores modernos.',
    history: [
      { id: 25, timestamp: '2026-07-08T11:20:00Z', changedBy: 'Diego Castillo', action: 'updated', environment: 'qa', summary: 'Corregidos contrastes en tablas y formularios.', previousState: 'qa: v4.3.2', newState: 'qa: v4.4.0' },
      { id: 24, timestamp: '2026-06-15T09:00:00Z', changedBy: 'Diego Castillo', action: 'enabled', environment: 'qa', summary: 'Promovido a QA — pruebas con 100 usuarios beta.', previousState: 'qa: false', newState: 'qa: true' },
      { id: 23, timestamp: '2026-06-01T10:00:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'development', summary: 'Desarrollo inicial del modo oscuro.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-013',
    key: 'eventos_en_vivo_v2',
    name: 'Eventos en Vivo V2',
    description: 'Versión mejorada de eventos en vivo con streaming de video, chat interactivo, encuestas en tiempo real y donaciones.',
    category: 'streaming',
    enabled: false,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.5.0',
    createdAt: '2026-06-10',
    updatedAt: '2026-07-10',
    updatedBy: 'María López',
    dependencies: [
      { key: 'live_chat_unificado', name: 'Chat Unificado', required: true },
      { key: 'stripe_pagos', name: 'Pagos con Stripe', required: false },
    ],
    modules: [
      { id: 'mod-eventos', name: 'Eventos', icon: 'ri-calendar-event-line', route: '/eventos' },
      { id: 'mod-eventos-en-vivo', name: 'Eventos en Vivo', icon: 'ri-live-line', route: '/eventos-en-vivo' },
      { id: 'mod-admin-events', name: 'Admin Eventos', icon: 'ri-calendar-2-line', route: '/admin/events' },
    ],
    documentation: 'Eventos en Vivo V2 agrega: streaming de video HD, chat interactivo con emojis, encuestas en tiempo real para la audiencia, sistema de donaciones con Stripe, y replay automático. Los eventos se pueden programar con anticipación y tienen página de aterrizaje propia.',
    history: [
      { id: 20, timestamp: '2026-07-10T08:45:00Z', changedBy: 'María López', action: 'updated', environment: 'development', summary: 'Integración de chat interactivo y encuestas completada.', previousState: 'v4.4.0', newState: 'v4.5.0' },
      { id: 19, timestamp: '2026-06-10T14:00:00Z', changedBy: 'María López', action: 'created', environment: 'development', summary: 'Desarrollo de Eventos en Vivo V2.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-014',
    key: 'autenticacion_2fa',
    name: 'Autenticación 2FA',
    description: 'Autenticación de dos factores para mayor seguridad en cuentas de administradores y super admins.',
    category: 'security',
    enabled: true,
    environments: { development: true, qa: true, production: false },
    minVersionRequired: '4.3.0',
    createdAt: '2026-04-01',
    updatedAt: '2026-07-05',
    updatedBy: 'Ana Rivera',
    dependencies: [],
    modules: [
      { id: 'mod-admin', name: 'Panel Admin', icon: 'ri-dashboard-line', route: '/admin/dashboard' },
      { id: 'mod-sa', name: 'Super Admin', icon: 'ri-admin-line', route: '/sa/dashboard' },
      { id: 'mod-settings', name: 'Configuración', icon: 'ri-settings-3-line', route: '/admin/settings' },
    ],
    documentation: '2FA usa TOTP (Time-based One-Time Password) compatible con Google Authenticator, Authy, y 1Password. Obligatorio para Super Admins. Opcional (recomendado) para Admins de radio. Códigos de recuperación de un solo uso disponibles.',
    history: [
      { id: 15, timestamp: '2026-07-05T10:30:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'qa', summary: 'Corregido bug de sincronización de tiempo TOTP.', previousState: 'qa: v4.2.8', newState: 'qa: v4.3.0' },
      { id: 14, timestamp: '2026-05-20T13:00:00Z', changedBy: 'Ana Rivera', action: 'enabled', environment: 'qa', summary: 'Promovido a QA — pruebas con equipo interno.', previousState: 'qa: false', newState: 'qa: true' },
      { id: 13, timestamp: '2026-04-01T09:00:00Z', changedBy: 'Ana Rivera', action: 'created', environment: 'development', summary: 'Desarrollo de 2FA iniciado.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-015',
    key: 'centova_auto_provisioning',
    name: 'CentovaCast Auto-Provisioning',
    description: 'Creación y gestión automática de cuentas de hosting en CentovaCast desde el panel SA.',
    category: 'streaming',
    enabled: false,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.5.0',
    createdAt: '2026-07-01',
    updatedAt: '2026-07-10',
    updatedBy: 'Carlos Mendoza',
    dependencies: [
      { key: 'stripe_pagos', name: 'Pagos con Stripe', required: true },
    ],
    modules: [
      { id: 'mod-sa-dashboard', name: 'Dashboard SA', icon: 'ri-dashboard-line', route: '/sa/dashboard' },
      { id: 'mod-sa-radios', name: 'Radios SA', icon: 'ri-radio-line', route: '/sa/radios' },
      { id: 'mod-sa-billing', name: 'Facturación SA', icon: 'ri-bill-line', route: '/sa/billing' },
    ],
    documentation: 'Auto-Provisioning de CentovaCast permite crear cuentas de hosting automáticamente cuando una radio compra un plan. Flujo: pago confirmado → se crea cuenta en CentovaCast → se asignan mount points → se envía email con credenciales. Soporta planes Free, Pro y Premium con diferentes límites.',
    history: [
      { id: 10, timestamp: '2026-07-10T17:00:00Z', changedBy: 'Carlos Mendoza', action: 'updated', environment: 'development', summary: 'Integración con API de CentovaCast completada.', previousState: 'v4.4.0', newState: 'v4.5.0' },
      { id: 9, timestamp: '2026-07-01T08:00:00Z', changedBy: 'Carlos Mendoza', action: 'created', environment: 'development', summary: 'Desarrollo de auto-provisioning CentovaCast.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-016',
    key: 'monetizacion_oyentes',
    name: 'Monetización para Oyentes',
    description: 'Sistema que permite a los oyentes ganar recompensas por su actividad y canjearlas por beneficios.',
    category: 'monetization',
    enabled: true,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.5.0',
    createdAt: '2026-06-15',
    updatedAt: '2026-07-09',
    updatedBy: 'María López',
    dependencies: [
      { key: 'gamificacion_total', name: 'Gamificación Total', required: true },
    ],
    modules: [
      { id: 'mod-gamificacion', name: 'Gamificación', icon: 'ri-gamepad-line', route: '/gamificacion' },
      { id: 'mod-monetization', name: 'Monetización Admin', icon: 'ri-money-dollar-circle-line', route: '/admin/monetization' },
      { id: 'mod-sa-payouts', name: 'Payouts SA', icon: 'ri-bank-line', route: '/sa/payouts' },
    ],
    documentation: 'Los oyentes acumulan Puntos RFW por: escuchar radios (1pt/min), reaccionar, compartir, invitar amigos, y completar logros. Los puntos se pueden canjear por: meses gratis de plan Pro, insignias exclusivas, merchandising, y entradas a eventos.',
    history: [
      { id: 5, timestamp: '2026-07-09T12:00:00Z', changedBy: 'María López', action: 'updated', environment: 'development', summary: 'Sistema de canje de puntos implementado.', previousState: 'v4.4.0', newState: 'v4.5.0' },
      { id: 4, timestamp: '2026-06-15T10:00:00Z', changedBy: 'María López', action: 'created', environment: 'development', summary: 'Desarrollo de monetización para oyentes.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-017',
    key: 'social_feed_v2',
    name: 'Social Feed V2',
    description: 'Feed social rediseñado con algoritmo de relevancia, hashtags, menciones, y contenido multimedia enriquecido.',
    category: 'social',
    enabled: true,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.4.0',
    createdAt: '2026-05-10',
    updatedAt: '2026-07-06',
    updatedBy: 'Diego Castillo',
    dependencies: [
      { key: 'live_chat_unificado', name: 'Chat Unificado', required: false },
    ],
    modules: [
      { id: 'mod-centro-social', name: 'Centro Social', icon: 'ri-chat-heart-line', route: '/centro-social' },
      { id: 'mod-social', name: 'Social', icon: 'ri-share-line', route: '/centro-social' },
      { id: 'mod-reacciones', name: 'Reacciones', icon: 'ri-emotion-line', route: '/reacciones' },
    ],
    documentation: 'Social Feed V2 introduce: algoritmo de relevancia basado en afinidad, soporte de hashtags con páginas de tendencias, menciones (@usuario) con notificaciones, encuestas nativas en el feed, y vista de galería para contenido multimedia.',
    history: [
      { id: 3, timestamp: '2026-07-06T14:30:00Z', changedBy: 'Diego Castillo', action: 'updated', environment: 'development', summary: 'Algoritmo de relevancia calibrado con datos reales.', previousState: 'v4.3.0', newState: 'v4.4.0' },
      { id: 2, timestamp: '2026-05-10T09:00:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'development', summary: 'Desarrollo del Social Feed V2.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-018',
    key: 'api_publica_v1',
    name: 'API Pública v1',
    description: 'API REST pública para desarrolladores que quieran integrar datos de radios, programas y rankings en sus aplicaciones.',
    category: 'core',
    enabled: false,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.5.0',
    createdAt: '2026-07-01',
    updatedAt: '2026-07-08',
    updatedBy: 'Ana Rivera',
    dependencies: [],
    modules: [
      { id: 'mod-api', name: 'API', icon: 'ri-code-line', route: '' },
      { id: 'mod-docs', name: 'Documentación', icon: 'ri-book-open-line', route: '' },
    ],
    documentation: 'API Pública v1 expone endpoints REST: GET /radios (listado con filtros), GET /radios/:id (detalle), GET /rankings (top por categoría/país), GET /programs (programación). Rate limit: 100 req/min por IP. Autenticación vía API Key.',
    history: [
      { id: 1, timestamp: '2026-07-08T10:00:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'development', summary: 'Documentación OpenAPI 3.0 generada.', previousState: 'v4.4.0', newState: 'v4.5.0' },
      { id: 0, timestamp: '2026-07-01T08:00:00Z', changedBy: 'Ana Rivera', action: 'created', environment: 'development', summary: 'Desarrollo de API Pública v1.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-019',
    key: 'programacion_automatica_ia',
    name: 'Programación Automática IA',
    description: 'Sistema de programación automática que usa IA para crear parrillas de programación óptimas basadas en datos de audiencia.',
    category: 'experimental',
    enabled: false,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.6.0',
    createdAt: '2026-07-01',
    updatedAt: '2026-07-11',
    updatedBy: 'Ana Rivera',
    dependencies: [
      { key: 'sistema_rankings', name: 'Sistema de Rankings', required: true },
      { key: 'ia_moderacion', name: 'IA Moderación', required: false },
    ],
    modules: [
      { id: 'mod-prog-auto', name: 'Programación Automática', icon: 'ri-timer-line', route: '/programacion-automatica' },
      { id: 'mod-schedule', name: 'Programación', icon: 'ri-calendar-schedule-line', route: '/admin/schedule' },
    ],
    documentation: 'La Programación Automática con IA analiza: patrones de audiencia históricos, rendimiento de programas anteriores, tendencias de género musical, y eventos especiales. Genera una parrilla semanal optimizada que maximiza retención de audiencia. Las radios pueden aceptar, modificar o rechazar la sugerencia.',
    history: [
      { id: 0, timestamp: '2026-07-11T16:00:00Z', changedBy: 'Ana Rivera', action: 'updated', environment: 'development', summary: 'Modelo de predicción de audiencia calibrado.', previousState: 'v4.5.0', newState: 'v4.6.0' },
      { id: -1, timestamp: '2026-07-01T10:00:00Z', changedBy: 'Ana Rivera', action: 'created', environment: 'development', summary: 'Desarrollo de programación automática IA.', previousState: '—', newState: 'dev: true' },
    ],
  },
  {
    id: 'ff-020',
    key: 'radio_website_builder',
    name: 'Radio Website Builder',
    description: 'Constructor de sitios web para radios. Permite crear una landing page personalizada sin código.',
    category: 'admin',
    enabled: false,
    environments: { development: true, qa: false, production: false },
    minVersionRequired: '4.6.0',
    createdAt: '2026-07-05',
    updatedAt: '2026-07-10',
    updatedBy: 'Diego Castillo',
    dependencies: [
      { key: 'stripe_pagos', name: 'Pagos con Stripe', required: false },
    ],
    modules: [
      { id: 'mod-settings', name: 'Configuración Admin', icon: 'ri-settings-3-line', route: '/admin/settings' },
      { id: 'mod-domain', name: 'Dominio', icon: 'ri-global-line', route: '/admin/settings/domain' },
    ],
    documentation: 'El Website Builder permite crear una landing page para la radio con: temas pre-diseñados (8 disponibles), editor drag & drop, secciones (hero, about, schedule, hosts, contact), dominio personalizado, y analytics integrados. Exportable como sitio estático.',
    history: [
      { id: 0, timestamp: '2026-07-10T13:30:00Z', changedBy: 'Diego Castillo', action: 'updated', environment: 'development', summary: 'Primeros 8 temas completados.', previousState: 'v4.5.0', newState: 'v4.6.0' },
      { id: -1, timestamp: '2026-07-05T09:00:00Z', changedBy: 'Diego Castillo', action: 'created', environment: 'development', summary: 'Desarrollo del Website Builder.', previousState: '—', newState: 'dev: true' },
    ],
  },
];

export const flagCategories = Object.keys(flagCategoryMeta) as FlagCategory[];

export function getFlagStats() {
  const total = featureFlags.length;
  const enabled = featureFlags.filter((f) => f.enabled).length;
  const inProd = featureFlags.filter((f) => f.environments.production).length;
  const inQa = featureFlags.filter((f) => f.environments.qa && !f.environments.production).length;
  const inDev = featureFlags.filter((f) => f.environments.development && !f.environments.qa && !f.environments.production).length;
  const hasDeps = featureFlags.filter((f) => f.dependencies.length > 0).length;
  return { total, enabled, inProd, inQa, inDev, hasDeps };
}